// const CRM = {
//   salesforce: 'salesforce',
//   hubspot: 'hubspot',
// };
import { L10nRevenueIntelligence } from 'helpers/l10n/L10RevenueIntelligence';
import reduxStore from 'helpers/reduxStore';

import { CUSTOM_CATEGORY, SMART_CATEGORY } from 'constants/deals';
import type { IRevIntelColumn } from 'types/revIntel';

interface ICrmDetermineParams {
  crmIntegration?: unknown;
  hubspotIntegration?: unknown;
  zendesksellIntegration?: unknown;
  copperIntegration?: unknown;
  pipedriveIntegration?: unknown;
  zohoIntegration?: unknown;
  freshsalesIntegration?: unknown;
}

interface ICrmLeadObject {
  type?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  data?: {
    name?: string;
    firstName?: string;
    lastName?: string;
    company?: string;
  };
}

interface ICrmFieldRef {
  uuid?: string | null;
  externalId?: string | null;
  fieldId?: string | null;
  fieldLabel?: string;
  dataType?: string;
  fieldType?: string;
  editableBy?: IRevIntelColumn['editableBy'];
  optionsJson?: IRevIntelColumn['options'];
}

interface IAvomaFieldRef {
  uuid?: string | null;
  externalId?: string | null;
  fieldId?: string | null;
  fieldLabel?: string;
  dataType?: string;
  fieldType?: string;
  optionsJson?: IRevIntelColumn['options'];
}

interface IDashboardField {
  uuid?: string | null;
  columnKey?: string | null;
  order?: number;
  groupName?: string;
  label?: string | null;
  isCrmData?: boolean;
  crmField?: ICrmFieldRef | null;
  avField?: IAvomaFieldRef | null;
  avomaField?: IAvomaFieldRef | null;
  dataType?: string;
  fieldType?: string;
  isEditable?: boolean;
  isReadOnly?: boolean;
  sortKey?: string | null;
  options?: IRevIntelColumn['options'];
  objectType?: string | null;
  orderBy?: IRevIntelColumn['orderBy'];
  filterKey?: string | null;
  referenceKey?: string | null;
  isSummarized?: boolean;
  headerTooltip?: string;
}

class Crm {
  static ENUM = {
    salesforce: 'salesforce',
    hubspot: 'hubspot',
    zsell: 'zsell',
    copper: 'copper',
    pipedrive: 'pipedrive',
    zoho: 'zoho',
    freshsales: 'freshsales'
  };

  static TAGS = {
    account: 'account',
    oppo: 'oppo',
    oppo_h: 'oppo_h',
    lead: 'lead',
    contact: 'contact'
  };

  static determine = ({
    crmIntegration,
    hubspotIntegration,
    zendesksellIntegration,
    copperIntegration,
    pipedriveIntegration,
    zohoIntegration,
    freshsalesIntegration
  }: ICrmDetermineParams): string | null => {
    let crm: string | null = null;
    if (zendesksellIntegration) {
      crm = Crm.ENUM.zsell;
    } else if (crmIntegration) {
      crm = Crm.ENUM.salesforce;
    } else if (hubspotIntegration) {
      crm = Crm.ENUM.hubspot;
    } else if (copperIntegration) {
      crm = Crm.ENUM.copper;
    } else if (pipedriveIntegration) {
      crm = Crm.ENUM.pipedrive;
    } else if (zohoIntegration) {
      crm = Crm.ENUM.zoho;
    } else if (freshsalesIntegration) {
      crm = Crm.ENUM.freshsales;
    } else {
      crm = null;
    }

    return crm;
  };

  static getCrmLeadName = (crmObject: ICrmLeadObject): string | null => {
    if (crmObject.type && crmObject.type !== 'lead') {
      return null;
    }
    let { name, firstName, lastName, company } = crmObject;
    if (crmObject.data) {
      firstName = crmObject.data.firstName;
      lastName = crmObject.data.lastName;
      company = crmObject.data.company;
      name = crmObject.data.name;
    }
    const names = [firstName, lastName];
    let fullName = '';
    if (name) {
      fullName = name;
    } else {
      fullName = names.join(' ');
    }
    return company ? `${fullName}, ${company}` : fullName;
  };

  static getCrmLeadFullName = (crmObject: ICrmLeadObject): string | null => {
    if (crmObject.type && crmObject.type !== 'lead') {
      return null;
    }

    const { name, firstName, lastName } = crmObject;
    return name || [firstName, lastName].join(' ');
  };
}

export const crmUserConnected = (crmProvider?: string): string => {
  const {
    hubspotUsername,
    zendesksellUsername,
    copperUsername,
    pipedriveUsername,
    zohoUsername,
    salesforceUsername
  } = reduxStore.getState().user;
  let crmUser = '';
  switch (crmProvider) {
    case 'salesforce':
      crmUser = salesforceUsername;
      break;
    case 'zsell':
      crmUser = zendesksellUsername;
      break;
    case 'hubspot':
      crmUser = hubspotUsername;
      break;
    case 'zoho':
      crmUser = zohoUsername;
      break;
    case 'pipedrive':
      crmUser = pipedriveUsername;
      break;
    case 'copper':
      crmUser = copperUsername;
      break;
    default:
      break;
  }

  return crmUser;
};

export const mergeCRMAndAVFields = (
  fields?: IDashboardField[]
): IRevIntelColumn[] =>
  fields?.map(field => ({
    uuid:
      field.crmField?.uuid ??
      field.avField?.uuid ??
      field.uuid ??
      field.columnKey ??
      '',
    columnKey: field.columnKey ?? null,
    order: field.order ?? 0,
    groupName:
      field.groupName === CUSTOM_CATEGORY
        ? SMART_CATEGORY
        : field.groupName ?? '',
    label:
      field.label ??
      field.crmField?.fieldLabel ??
      field.avField?.fieldLabel ??
      field.columnKey ??
      field.uuid ??
      L10nRevenueIntelligence.dashboard.columns.unnamedField,
    isCrmData: field.isCrmData ?? !!field.crmField,
    dataType:
      field.dataType ??
      field.crmField?.dataType ??
      field.avField?.dataType ??
      '',
    fieldType:
      field.fieldType ??
      field.crmField?.fieldType ??
      field.avField?.fieldType ??
      '',
    isEditable: field.isEditable ?? false,
    isReadOnly: field.isReadOnly ?? false,
    editableBy: field.crmField?.editableBy,
    sortKey: field.sortKey ?? null,
    options:
      field.crmField?.optionsJson ??
      field.avField?.optionsJson ??
      field.options ??
      [],
    objectType: field.objectType ?? null,
    avomaField: field.avomaField ?? field.avField ?? null,
    avField: field.avField ?? null,
    crmField: field.crmField ?? null,
    orderBy: field.orderBy ?? null,
    filterKey: field.filterKey,
    referenceKey: field.referenceKey ?? null,
    isSummarized: field.isSummarized ?? false,
    headerTooltip: field.headerTooltip
  })) ?? [];

export default Crm;
